import { parsePhoneNumberFromString } from "libphonenumber-js"

export function yesNo(value: boolean | undefined | null): string {
  if (value === true) return "Yes"
  if (value === false) return "No"
  return ""
}

export function capitalize(string: string): string {
  return string[0].toUpperCase() + string.slice(1).toLowerCase()
}

export function removeUnderscores(string: string): string {
  return string.replace(/_/g, " ")
}

/**
 * Converts a delimited identifier into sentence case: hyphens and underscores
 * become spaces, the first character is upper-cased and the rest lower-cased.
 *
 * @example sentenceCase('payment-reviews-scheduler') // 'Payment reviews scheduler'
 */
export function sentenceCase(string: string): string {
  const words = string.replace(/[-_]+/g, " ").trim()
  if (!words) return ""
  return words[0].toUpperCase() + words.slice(1).toLowerCase()
}

export function camelCaseToWords(string: string): string {
  return string
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (match) => match.toUpperCase())
}

export const isHTML = (inputString: string): boolean => {
  const htmlTags = /<[^>]*>/
  return htmlTags.test(inputString)
}

export const includes = (string1: string, string2: string) => {
  return string1.toLowerCase().includes(string2.toLowerCase())
}

export const toNumberOrNull = (str: string) => {
  const num = Number(str)
  if (isNaN(num)) return null
  return num
}
export const safeJSONParse = (string: string) => {
  try {
    return JSON.parse(string)
  } catch (err) {
    return string
  }
}

export const isSvgPath = (value: unknown): value is string => {
  return typeof value === "string" && /^M[\d., -]+/i.test(value)
}

/**
 * Extracts the root domain from a given hostname.
 *
 * @param hostname - The full hostname from which to extract the root domain.
 * @returns The root domain of the given hostname.
 *
 * @example
 * ```typescript
 * rootDomain('sub.example.co.uk'); // Returns 'example.co.uk'
 * rootDomain('example.com'); // Returns 'example.com'
 * rootDomain('sub.example.com'); // Returns 'example.com'
 * ```
 */
export const rootDomain = (hostname: string) => {
  const doubleTlds = ["co", "com"]
  let parts = hostname.split(".")

  if (parts.length <= 2) return hostname

  parts = parts.slice(-3)

  if (doubleTlds.indexOf(parts[1]) > -1) return parts.join(".")

  return parts.slice(-2).join(".")
}

/**
 * Formats a UK phone number into E.164 international format.
 *
 * Accepts a UK phone number in either national format (e.g., "07123456789") or
 * international format (e.g., "+447123456789") and returns the number in
 * standardized E.164 format (e.g., "+447123456789").
 *
 * @param phoneNumber - The phone number to format, expected to be a UK number.
 * @returns The phone number in E.164 format, or `null` if the number is invalid.
 */
export const formatUkPhoneToE164 = (phoneNumber: string): string | null => {
  if (!phoneNumber) return null

  const parsed = parsePhoneNumberFromString(phoneNumber, "GB")
  return parsed?.isValid() ? parsed.format("E.164") : null
}

/**
 * Formats a person's name for display.
 *
 * Junifer stores most names in upper case (~70% of contacts), whereas ark
 * title-cased them at sync time — so Nova has always shown title case. Format
 * on the way out only: edit forms stay bound to the raw value, so we never
 * write a normalised-but-wrong name back to Junifer.
 *
 * Splits on spaces, apostrophes and hyphens, so "O'BRIEN" becomes "O'Brien"
 * rather than ark's naive "Obrien". "MCDONALD" still becomes "Mcdonald" —
 * unavoidable without a name dictionary, and display-only.
 */
export function toDisplayName(value: string | null | undefined): string {
  if (!value) return ""
  return value
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(
      /(^|[\s'-])(\p{L})/gu,
      (_, boundary, letter) => boundary + letter.toUpperCase(),
    )
}
